import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUpload({ value, onChange, token, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const inputRef = useRef(null);

  // Sync preview with value prop when it changes
  useEffect(() => {
    setPreview(value || "");
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("FORMATO NO VÁLIDO. USA JPG, PNG, GIF O WEBP");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("IMAGEN DEMASIADO GRANDE (MÁX 5MB)");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      // Get token - fallback to localStorage if prop is not available
      const authToken = token || localStorage.getItem("token");
      if (!authToken) {
        toast.error("NO AUTENTICADO - INICIA SESIÓN DE NUEVO");
        return;
      }

      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setPreview(imageUrl);
      onChange(imageUrl);
      toast.success("IMAGEN SUBIDA");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "ERROR AL SUBIR IMAGEN");
      setPreview(value || "");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setPreview("");
    onChange("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        id="image-upload"
      />
      
      {preview ? (
        <div className="relative group">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-48 object-cover border border-zinc-800"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="border-white text-white hover:bg-white hover:text-black"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              CAMBIAR
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-48 border-2 border-dashed border-zinc-700 hover:border-primary transition-colors flex flex-col items-center justify-center gap-3 bg-[#0a0a0a]"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <span className="font-mono text-sm text-zinc-500">SUBIENDO...</span>
            </>
          ) : (
            <>
              <ImageIcon className="w-8 h-8 text-zinc-600" />
              <span className="font-mono text-sm text-zinc-500">SUBIR IMAGEN</span>
              <span className="font-mono text-xs text-zinc-600">JPG, PNG, GIF, WEBP (máx 5MB)</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
